// Homepage hero subscription cards (see
// snippets/wrpg-hero-subscription-cards.liquid): on desktop the two cards
// are stacked on top of each other via CSS, with the back one peeking out
// at an angle. This toggles which card has `.is-front` and keeps the
// stack sized so it always fills ~80% of the hero's own height, growing
// wider too (up to 2x its reference width) whenever there's real room for
// it before the heading/signup box.
//
// Why this needs measure-then-scale (not just a fixed size): the cards'
// text (bullet points, title, perk line) is translated and its length
// varies, so a fixed box would either clip content or leave dead space.
// Instead this measures the stack's own natural, unscaled size at a given
// width, then applies a single `transform: scale()` to reach the target
// height - since every internal font-size/padding/gap is in `rem`, scaling
// the whole subtree keeps everything in proportion (bigger card, bigger
// text) and can never overflow, because the scale factor is derived FROM
// the measured natural size rather than guessed independently of it.
//
// Width is handled separately from that height-driven scale: rather than
// always keeping the reference card's own aspect ratio (which reads as
// "stretched" once scaled tall enough to fill 80% of a tall hero), this
// also measures the card at up to double its reference width - letting
// its *content* reflow wider, not just get uniformly bigger - and uses
// whichever width (interpolated between those two measurements) fits the
// real, measured gap before the heading/signup box. Height is always
// re-derived from a fresh measurement at whatever width is finally
// chosen, so it stays pinned to the 80% target regardless of width.
//
// Why JS has to own sizing at all: on desktop the cards are `position:
// absolute` (so they can overlap for the peek effect), which means they
// don't contribute to their `position: relative` parent's size at all.
//
// Below the mobile breakpoint the snippet's CSS drops the absolute
// stacking and shows one card at a time in a snap row (swipe + arrows).
// Desktop sizing is left to this file; mobile scroll is native overflow.
(() => {
  const DESKTOP_QUERY = '(min-width: 750px)';
  const HEIGHT_FRACTION = 0.8; // target: cards fill 80% of the hero's height
  const MAX_WIDTH_MULTIPLIER = 2; // cards may widen up to 2x their reference width
  const MIN_SCALE = 0.6;
  const MAX_SCALE = 2.4;

  function isDesktop() {
    return window.matchMedia(DESKTOP_QUERY).matches;
  }

  function layoutStack(stack, clip, cards) {
    if (!isDesktop()) {
      stack.style.width = '';
      stack.style.height = '';
      clip.style.width = '';
      clip.style.height = '';
      clip.style.transform = '';
      return;
    }

    const hero = stack.closest('.banner__content--hero');
    if (!hero) return;

    // Reset to the natural, unscaled size before measuring - otherwise a
    // stale transform/size from a previous measurement would throw this
    // measurement off.
    clip.style.transform = 'none';
    clip.style.width = '';
    clip.style.height = '';
    stack.style.width = '';
    stack.style.height = '';

    const front = clip.querySelector('.is-front') || cards[0];
    if (!front) return;

    const baseWidth = clip.offsetWidth; // reference width from CSS (19rem)
    if (!baseWidth) return;

    const heroRect = hero.getBoundingClientRect();
    if (!heroRect.height) return;
    const targetHeight = heroRect.height * HEIGHT_FRACTION;

    // Sets the clip to a candidate width, measures the front card's real
    // (reflowed) natural height at that width, and returns the scale
    // needed to bring that height exactly to targetHeight.
    const measureScaleAt = (width) => {
      clip.style.width = `${width}px`;
      const naturalHeight = front.offsetHeight;
      if (!naturalHeight) return null;
      return { naturalHeight, scale: targetHeight / naturalHeight };
    };

    const base = measureScaleAt(baseWidth);
    if (!base) return;
    const visualWidth0 = baseWidth * base.scale;

    // Try doubling the width: letting the card reflow wider (not just get
    // uniformly bigger) is what makes it look like a card instead of a
    // stretched strip once it's scaled tall enough to fill 80% of the
    // hero - this re-measures rather than guessing, since text reflow
    // across the extra width isn't predictable from CSS alone.
    const wideWidth = baseWidth * MAX_WIDTH_MULTIPLIER;
    const wide = measureScaleAt(wideWidth) || base;
    const visualWidth1 = wideWidth * wide.scale;

    // Real available room before the heading/signup box (its own
    // guaranteed-gap margin is already baked into this rect) - not a
    // guessed fraction of the hero's width.
    const textBox = hero.querySelector('.banner__box--hero');
    const stackRect = stack.getBoundingClientRect();
    let availableWidth = visualWidth1;
    if (textBox) {
      const textBoxRect = textBox.getBoundingClientRect();
      if (textBoxRect.width) {
        availableWidth = textBoxRect.left - stackRect.left;
      }
    }

    let width = baseWidth;
    let result = base;
    if (availableWidth > visualWidth0 && visualWidth1 > visualWidth0) {
      // Linear interpolation between the two measured points - text
      // reflow height isn't perfectly linear in width, but this is a
      // reasonable, cheap approximation for how much wider to go.
      const targetVisualWidth = Math.min(availableWidth, visualWidth1);
      const t = (targetVisualWidth - visualWidth0) / (visualWidth1 - visualWidth0);
      width = baseWidth + (wideWidth - baseWidth) * t;
      result = measureScaleAt(width) || base;

      // The interpolation is approximate - if it overshot the real
      // available room, back off once with a direct correction.
      const actualVisualWidth = width * result.scale;
      if (actualVisualWidth > availableWidth && actualVisualWidth > 0) {
        width *= availableWidth / actualVisualWidth;
        result = measureScaleAt(width) || result;
      }
    }

    const scale = Math.min(Math.max(result.scale, MIN_SCALE), MAX_SCALE);

    clip.style.width = `${width}px`;
    clip.style.height = `${result.naturalHeight}px`;
    clip.style.transform = `scale(${scale})`;

    stack.style.width = `${width * scale}px`;
    stack.style.height = `${result.naturalHeight * scale}px`;
  }

  function swapFront(stack, clip, cards) {
    const front = clip.querySelector('.is-front');
    const next = cards.find((card) => card !== front) || cards[0];
    if (!next || next === front) return;
    if (front) front.classList.remove('is-front');
    next.classList.add('is-front');
    layoutStack(stack, clip, cards);
  }

  document.querySelectorAll('[data-wrpg-hero-stack]').forEach((stack) => {
    const clip = stack.querySelector('[data-wrpg-hero-clip]');
    if (!clip) return;
    const cards = Array.from(clip.querySelectorAll('[data-wrpg-hero-card]'));
    if (!cards.length) return;

    layoutStack(stack, clip, cards);
    window.addEventListener('resize', () => layoutStack(stack, clip, cards));

    // Re-measure if a card's own content changes size after first paint
    // (webfont swap, image finishing decode, etc.) instead of trusting a
    // single measurement taken too early.
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => layoutStack(stack, clip, cards));
      cards.forEach((card) => observer.observe(card));
    }

    if (cards.length < 2) return;

    cards.forEach((card) => {
      card.addEventListener('click', () => {
        if (!isDesktop()) return;
        // Clicks on the front card's own controls (image/button) behave
        // normally; clicking the peeking back card brings it to the front
        // instead of trying to activate its (barely visible) button.
        if (card.classList.contains('is-front')) return;
        swapFront(stack, clip, cards);
      });
    });

    const swapButton = stack.querySelector('[data-wrpg-hero-swap]');
    if (swapButton) {
      swapButton.addEventListener('click', (event) => {
        event.stopPropagation();
        swapFront(stack, clip, cards);
      });
    }

    const prevButton = stack.querySelector('[data-wrpg-hero-prev]');
    const nextButton = stack.querySelector('[data-wrpg-hero-next]');
    if (prevButton || nextButton) {
      const cardIndex = () => {
        const origin = clip.getBoundingClientRect().left;
        let best = 0;
        let bestDist = Infinity;
        cards.forEach((card, index) => {
          const dist = Math.abs(card.getBoundingClientRect().left - origin);
          if (dist < bestDist) {
            bestDist = dist;
            best = index;
          }
        });
        return best;
      };
      const goTo = (index) => {
        const next = cards[Math.max(0, Math.min(cards.length - 1, index))];
        if (!next) return;
        const left = next.getBoundingClientRect().left - clip.getBoundingClientRect().left + clip.scrollLeft;
        clip.scrollTo({ left, behavior: 'smooth' });
      };
      const syncNav = () => {
        const index = cardIndex();
        if (prevButton) prevButton.disabled = index <= 0;
        if (nextButton) nextButton.disabled = index >= cards.length - 1;
      };
      prevButton?.addEventListener('click', (event) => {
        event.stopPropagation();
        goTo(cardIndex() - 1);
      });
      nextButton?.addEventListener('click', (event) => {
        event.stopPropagation();
        goTo(cardIndex() + 1);
      });
      clip.addEventListener('scroll', syncNav, { passive: true });
      window.addEventListener('resize', syncNav);
      syncNav();
    }
  });
})();
